import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Button, Grid, ListItem, Typography } from '@mui/material';
import { BusinessCenter } from '@mui/icons-material';
import { Stack } from '@mui/system';

import firebaseHooks from '../../firebase/hooks';
import {
  AddCompanyDrawer,
  Container,
  CustomerRecipientsDrawer,
  QuestionDrawer,
  Show,
} from '../../components';
import { useCompany, useForm, usePagination, useSorting } from '../../hooks';
import { DataTable, DataTableColumn } from '../../tables';
import { useDataCollection } from '../../hooks/useDataCollection';
import { AgencyCustomer, AssociatedCompany } from '../../types';
import { StyledTextButton } from '../Button/Button.component';
import { CustomerShipToDrawer } from '../CustomerShipTo/CustomerShipToDrawer.component';
import { CompanyType, ModificationMode } from '../../enums';
import getCompanySchema from '../../validation/CompanyValidations';
import { Company } from '../../models';
import { AgencyCustomersListProps } from './AgencyCustomersList.props';
import { AssociatedCompanyMenuItems } from './AssociatedCompanyMenuItems.component';

interface SideDrawerDisplay {
  customer: AgencyCustomer | null;
  type: 'shipTo' | 'recipients' | null;
}

const MAX_RECIPENTS_DISPLAY = 2;

export const AgencyCustomersList = ({
  agencyId,
  hiddenProperties,
}: AgencyCustomersListProps) => {
  const { t } = useTranslation();
  const sorting = useSorting('companyName');
  const pagination = usePagination();
  const [sideDrawerDisplay, setSideDrawerDisplay] = useState<SideDrawerDisplay>(
    { type: null, customer: null }
  );
  const [showAddCompanyForm, setShowAddCompanyForm] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] =
    useState<AssociatedCompany | null>(null);
  const agency = useCompany(agencyId || '');

  const companies: AgencyCustomer[] = useMemo(() => {
    if (agency.loading || !agency.item || !agency.item.associatedCompanies) {
      return [];
    }

    return Object.values(agency.item.associatedCompanies)
      .filter((a) => !a.isDeleted)
      .map((company) => {
        return {
          ...company,
          id: company.companyId,
          shipToAmount: company.customerAddresses?.length || 0,
          companyAccountNumber:
            company.companyAccountNumber || t('common.notApplicable'),
        };
      });
  }, [agency, t]);

  const { list: data } = useDataCollection(companies, sorting, pagination);

  const nameOutput = (id: string) => {
    const customer = companies.find((company) => company.id === id) ?? null;
    return (
      <Typography>
        {customer?.companyName || t('common.notApplicable')}
      </Typography>
    );
  };

  const shipToOutput = (shipTos: number, customer: AgencyCustomer) =>
    shipTos === 0 ? (
      <Typography>{shipTos}</Typography>
    ) : (
      <StyledTextButton
        onClick={() => setSideDrawerDisplay({ customer, type: 'shipTo' })}
      >
        {t('agencies.shipTo.label', { count: shipTos })}
      </StyledTextButton>
    );

  const renderButtonText = (
    text: string,
    customer: AgencyCustomer | null,
    key?: string
  ) => (
    <ListItem key={key} sx={{ width: 'inherit', px: 0 }}>
      <StyledTextButton
        onClick={() => setSideDrawerDisplay({ customer, type: 'recipients' })}
      >
        {text}
      </StyledTextButton>
    </ListItem>
  );

  const recipientsOutput = (customer: AgencyCustomer) => (
    <Stack direction="column" sx={{ width: 'inherit' }}>
      {!customer.shippingDocEmailRecipient?.length
        ? renderButtonText(t('agencies.fields.emptyRecipients'), customer)
        : (customer.shippingDocEmailRecipient || [])
            .slice(0, MAX_RECIPENTS_DISPLAY)
            .map((email, key) =>
              renderButtonText(email, customer, email + key)
            )}
      {(customer.shippingDocEmailRecipient || []).length >
        MAX_RECIPENTS_DISPLAY &&
        renderButtonText(
          t('agencies.emailRecipients.seeAll', {
            count: (customer.shippingDocEmailRecipient || []).length,
          }),
          customer
        )}
    </Stack>
  );

  const resetCustomer = () => {
    setSideDrawerDisplay({ customer: null, type: null });
  };

  const firestore = firebaseHooks.useFirestore();
  const companyValidations = useMemo(
    () => getCompanySchema(firestore, agency.item?.type),
    [agency.item?.type, firestore]
  );
  const form = useForm(agency, {
    mode: ModificationMode.Edit,
    validations: companyValidations,
  });

  const handleAddCompany = (company: Company) => {
    if (!form.item) return;

    const newAssociatedCompany: AssociatedCompany = {
      companyId: company.id,
      companyName: company.name,
      companyAccountNumber: company.accountNumber || '',
      isDeleted: false,
      customerAddresses: [],
      shippingDocEmailRecipient: null,
    };

    form.setItem({
      ...form.item,
      associatedCompanies: {
        ...form.item?.associatedCompanies,
        [company.id]: newAssociatedCompany,
      },
    } as Company);

    setSaveInProgress(true);
  };

  const handleDeleteCompany = () => {
    if (!form.item || !companyToDelete) return;

    const updatedAssociatedCompanies = {
      ...form.item.associatedCompanies,
      [companyToDelete.companyId]: {
        ...companyToDelete,
        isDeleted: true,
      },
    };

    form.setItem({
      ...form.item,
      associatedCompanies: updatedAssociatedCompanies,
    } as Company);

    setSaveInProgress(true);
    setShowDeleteConfirm(false);
    setCompanyToDelete(null);
  };

  useEffect(() => {
    if (form.dirty && saveInProgress) {
      setSaveInProgress(false);
      form.save();
    }
  }, [form, saveInProgress]);

  const cancelConfirm = () => {
    setShowDeleteConfirm(false);
    setCompanyToDelete(null);
  };

  const hideAddButton = Boolean(
    agency.item?.type !== CompanyType.CorporateCompany
  );

  const menuItems = (company: AssociatedCompany) => (
    <AssociatedCompanyMenuItems
      company={company}
      onDeleteClick={(company: AssociatedCompany) => {
        setCompanyToDelete(company);
        setShowDeleteConfirm(true);
      }}
    />
  );

  return (
    <Container sx={{ marginTop: 0 }}>
      <Grid display="flex" justifyContent={'flex-end'} alignItems="end">
        {!hideAddButton && (
          <Button
            variant="contained"
            onClick={() => setShowAddCompanyForm(true)}
          >
            {t('companies.actions.addCompany')}
          </Button>
        )}
      </Grid>
      <Grid sx={{ marginTop: 2 }}>
        <DataTable
          data={data}
          menuItems={menuItems}
          pagination={pagination}
          sorting={sorting}
        >
          <DataTableColumn
            title={t(
              `${
                agency.item?.type === CompanyType.Agency
                  ? 'agencies'
                  : 'corporateCompanies'
              }.fields.customer`
            )}
            hidden={hiddenProperties?.includes('companyId')}
            property="companyId"
            width="25%"
            output={nameOutput}
          />
          <DataTableColumn
            hidden={hiddenProperties?.includes('companyAccountNumber')}
            property="companyAccountNumber"
            title={t('companies.fields.accountNumber')}
            width="20%"
          />
          <DataTableColumn
            hidden={hiddenProperties?.includes('shipToAmount')}
            property="shipToAmount"
            title={t('agencies.fields.shipTo')}
            width="20%"
            output={shipToOutput}
          />
          <DataTableColumn
            hidden={hiddenProperties?.includes('emailRecipients')}
            disableSort
            property="emailRecipients"
            title={t('agencies.fields.emailRecipients')}
            width="35%"
            output={(_, customer: AgencyCustomer) => recipientsOutput(customer)}
          />
        </DataTable>
      </Grid>
      <Show
        if={!!sideDrawerDisplay.customer && sideDrawerDisplay.type === 'shipTo'}
      >
        <CustomerShipToDrawer
          customer={sideDrawerDisplay.customer}
          onClose={resetCustomer}
        />
      </Show>
      <Show
        if={
          !!sideDrawerDisplay.customer &&
          sideDrawerDisplay.type === 'recipients'
        }
      >
        <CustomerRecipientsDrawer
          agency={agency}
          customer={sideDrawerDisplay.customer}
          onClose={resetCustomer}
        />
      </Show>
      <Show if={showAddCompanyForm}>
        <AddCompanyDrawer
          currentCompany={agency.item}
          onCancelClicked={() => setShowAddCompanyForm(false)}
          onDoneClicked={(company) => {
            setShowAddCompanyForm(false);

            if (company) {
              handleAddCompany(company);
            }
          }}
        />
      </Show>
      <QuestionDrawer
        disabled={saveInProgress}
        open={showDeleteConfirm}
        icon={<BusinessCenter sx={{ color: 'secondary.main' }} />}
        message={t('associatedCompanies.deleteAssociatedCompany.message')}
        onConfirm={() => handleDeleteCompany()}
        onCancel={() => cancelConfirm()}
        title={t('associatedCompanies.deleteAssociatedCompany.title')}
      />
    </Container>
  );
};
